// Synthesised chimes for pack reveal and check-in success (round-4 finish-list
// item: "the app should make a sound when the user opens a pack of cards and
// logs a game… small chime or ding"). No binary assets — the approved-assets
// registry is untouched — the tone is built from WebAudio oscillators + a
// gain envelope at call time. Every call is defensively wrapped so that any
// failure (no AudioContext, suspended context, throw) is swallowed silently:
// sound must never break a reward flow (CLAUDE.md rule 3).

const PEAK_GAIN = 0.2;

let sharedCtx = null;

function ensureContext() {
  if (sharedCtx) return sharedCtx;
  const Ctor = globalThis.AudioContext || globalThis.webkitAudioContext;
  if (typeof Ctor !== "function") return null;
  sharedCtx = new Ctor();
  return sharedCtx;
}

function scheduleNote(ctx, destination, frequency, startAt, duration) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(frequency, startAt);
  const attack = Math.min(0.012, duration * 0.2);
  const release = duration - attack;
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(PEAK_GAIN, startAt + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + attack + release);
  osc.connect(gain).connect(destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
}

// Two-note major-third ding (E5 → G#5) — the check-in "logged a game"
// success cue. < 300 ms total.
function playCheckin(ctx) {
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(1, now);
  master.connect(ctx.destination);
  const base = 659.25;
  const majThird = base * Math.pow(2, 4 / 12);
  scheduleNote(ctx, master, base, now, 0.22);
  scheduleNote(ctx, master, majThird, now + 0.12, 0.26);
}

function playPack(ctx) {
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(1, now);
  master.connect(ctx.destination);
  const c5 = 523.25;
  const e5 = 659.25;
  const g5 = 783.99;
  scheduleNote(ctx, master, c5, now, 0.18);
  scheduleNote(ctx, master, e5, now + 0.12, 0.2);
  scheduleNote(ctx, master, g5, now + 0.24, 0.3);
}

// Prime the shared AudioContext inside the user gesture that starts a reward
// flow (open/claim/check-in). Browsers only allow an AudioContext to start
// (or resume) when it is created/resumed within a live user-activation event;
// playChime is reached AFTER an awaited network call, so creating the context
// there left it outside the gesture and never allowed to start. primeAudio is
// called synchronously at the TOP of each triggering click handler (before any
// await) so the context is unlocked while the gesture is still live. Failure-
// silent: returns the context or null, never throws.
export function primeAudio() {
  try {
    const ctx = ensureContext();
    if (!ctx) return null;
    if (typeof ctx.resume === "function" && ctx.state === "suspended") {
      Promise.resolve(ctx.resume()).catch(() => {});
    }
    return ctx;
  } catch {
    return null;
  }
}

const RESUME_TIMEOUT_MS = 1000;

// A short guard so an unresponsive resume() never blocks the chime path
// indefinitely. Resolves on resume OR timeout; rejects only when resume itself
// rejects (so playChime can bail out of scheduling without throwing).
function waitForResume(ctx) {
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(resolve, RESUME_TIMEOUT_MS); });
  return Promise.race([Promise.resolve(ctx.resume()), timeout]).finally(() => clearTimeout(timer));
}

export async function playChime(kind) {
  try {
    const ctx = ensureContext();
    if (!ctx) return;
    if (typeof ctx.resume === "function" && ctx.state === "suspended") {
      try {
        await waitForResume(ctx);
      } catch {
        return; // resume rejected — never schedule on a disallowed context.
      }
    }
    if (kind === "checkin") playCheckin(ctx);
    else if (kind === "pack") playPack(ctx);
  } catch {
    // Swallowed on purpose — see module header.
  }
}